import { api } from './api';
import type { EventType, AuditLog } from './database.types';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Session ID for grouping events (persisted in sessionStorage)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('nidastudio_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('nidastudio_session_id', sessionId);
  }
  return sessionId;
};

export const analytics = {
  // Track Page Views
  logPageView: async (path: string) => {
    if (path.startsWith('/admin')) return;

    // Push to Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title
      });
    }

    try {
      await api.post('/analytics/events', {
        event_type: 'page_view',
        path,
        session_id: getSessionId(),
        metadata: { timestamp: new Date().toISOString() }
      });
    } catch (e) { }
  },

  // Track Article Engagement
  logArticleEngagement: async (id: string, depthPercent: number, timeSpentSeconds: number) => {
    // Push to Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'article_read', {
        article_id: id,
        depth_percent: depthPercent,
        time_spent_seconds: timeSpentSeconds
      });
    }

    try {
      await api.post('/analytics/events', {
        event_type: 'article_read',
        content_id: id,
        session_id: getSessionId(),
        metadata: {
          depth_percent: depthPercent,
          time_spent_seconds: timeSpentSeconds,
          timestamp: new Date().toISOString()
        }
      });
    } catch (e) { }
  },

  // Track Audio Play
  logAudioPlay: async (id: string) => {
    // Push to Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'media_play', {
        content_id: id,
        media_type: 'audio'
      });
    }

    try {
      await api.post('/analytics/events', {
        event_type: 'media_play',
        content_id: id,
        session_id: getSessionId(),
        metadata: {
          media_type: 'audio',
          timestamp: new Date().toISOString()
        }
      });
    } catch (e) { }
  },

  // Track Audio Duration
  logAudioListen: async (id: string, seconds: number) => {
    if (seconds <= 0) return;
    try {
      await api.post('/analytics/events', {
        event_type: 'media_listen',
        content_id: id,
        session_id: getSessionId(),
        metadata: {
          listened_seconds: seconds,
          timestamp: new Date().toISOString()
        }
      });
    } catch (e) { }
  },

  // Get analytics stats
  getStats: async () => {
    try {
      return await api.get<any>('/analytics/stats');
    } catch (err) {
      return {
        totalPageViews: 0,
        totalArticleReads: 0,
        totalMediaPlays: 0,
        topArticles: [],
        recentEvents: 0
      };
    }
  }
};

export const auditLog = {
  log: async (action: string, actorId: string, actorName: string, details?: string, contentId?: string, contentType?: string, metadata?: any) => {
    try {
      await api.post('/auth/audit-logs', {
        action,
        actor_id: actorId,
        actor_name: actorName,
        details,
        content_id: contentId,
        content_type: contentType,
        metadata
      });
    } catch (e) { }
  },

  getLogs: async (limit: number = 50, offset: number = 0) => {
    try {
      return await api.get<AuditLog[]>('/auth/audit-logs', { limit, offset });
    } catch (err) {
      return [];
    }
  }
};